/**
 * Ein Beitrag – dieselbe Darstellung im Team-Chat und am Vorgang.
 *
 * TEAM-CHAT.md, Abschnitt 3: „identische Optik wie im Chat". Deshalb eine
 * Komponente und nicht zwei: Wer die Sprechblase ändert, ändert sie an
 * beiden Orten, und die beiden können nicht auseinanderlaufen.
 *
 * Unterschiede gibt es nur zwei, und sie stehen als Schalter drin: Am
 * Vorgang zählt nicht, wer online ist, und es gibt keinen Lesehaken – dort
 * zählt, was gesagt wurde.
 */

import { useState } from 'react';
import type { Beitrag as Daten } from '../typen';
import { IconHaken } from './Icons';
import { emojiZerlegen } from './Emoji';

/**
 * Den Text in Stücke zerlegen, damit `@Name` hervorgehoben werden kann.
 *
 * Gesucht wird nur nach Namen, die der Server als Erwähnung aufgelöst hat –
 * so wird aus einer Mailadresse im Text keine falsche Erwähnung, und ein
 * Name, den es nicht mehr gibt, bleibt schlicht Text.
 */
function teile(text: string, erwaehnt: string[], ich: string): { t: string; art: '' | 'ich' | 'andere' }[] {
  if (!erwaehnt.length) return [{ t: text, art: '' }];
  const sortiert = [...erwaehnt].sort((a, b) => b.length - a.length);
  const muster = new RegExp(`@(${sortiert.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'gi');
  const raus: { t: string; art: '' | 'ich' | 'andere' }[] = [];
  let zuletzt = 0;
  for (const treffer of text.matchAll(muster)) {
    const i = treffer.index ?? 0;
    if (i > zuletzt) raus.push({ t: text.slice(zuletzt, i), art: '' });
    const name = treffer[1] ?? '';
    raus.push({ t: `@${name}`, art: name.toLowerCase() === ich.toLowerCase() ? 'ich' : 'andere' });
    zuletzt = i + treffer[0].length;
  }
  if (zuletzt < text.length) raus.push({ t: text.slice(zuletzt), art: '' });
  return raus;
}

/**
 * Gewöhnlichen Text setzen – mit größeren Emoji und markierten Suchtreffern.
 *
 * Beides zerlegt denselben Text, deshalb nacheinander in einer Funktion:
 * erst die Emoji herausschneiden, dann in den übrigen Stücken den Suchbegriff
 * hervorheben. Umgekehrt zerschnitte die Suche ein zusammengesetztes Emoji.
 */
function Textstueck({ t, suche }: { t: string; suche: string }) {
  return (
    <>
      {emojiZerlegen(t).map((s, i) =>
        s.emoji ? (
          <span className="bt-emoji" key={i}>
            {s.t}
          </span>
        ) : (
          <Hervorgehoben text={s.t} suche={suche} key={i} />
        ),
      )}
    </>
  );
}

function Hervorgehoben({ text, suche }: { text: string; suche: string }) {
  if (!suche) return <>{text}</>;
  const stuecke: { t: string; treffer: boolean }[] = [];
  const klein = text.toLowerCase();
  const was = suche.toLowerCase();
  let ab = 0;
  for (;;) {
    const i = klein.indexOf(was, ab);
    if (i < 0) break;
    if (i > ab) stuecke.push({ t: text.slice(ab, i), treffer: false });
    stuecke.push({ t: text.slice(i, i + was.length), treffer: true });
    ab = i + was.length;
  }
  if (!stuecke.length) return <>{text}</>;
  if (ab < text.length) stuecke.push({ t: text.slice(ab), treffer: false });
  return (
    <>
      {stuecke.map((s, i) => (s.treffer ? <mark className="bt-treffer" key={i}>{s.t}</mark> : <span key={i}>{s.t}</span>))}
    </>
  );
}

function Sprechblase({ b, ich, klein, suche = '' }: { b: Daten; ich: string; klein?: boolean; suche?: string }) {
  return (
    <span className={`bt-blase ${b.eigen ? 'eigen' : ''} ${klein ? 'klein' : ''}`}>
      {teile(b.text, b.erwaehnt, ich).map((s, i) =>
        s.art ? (
          <span key={i} className={`bt-erwaehnung ${s.art}`}>
            {s.t}
          </span>
        ) : (
          <Textstueck t={s.t} suche={suche} key={i} />
        ),
      )}
    </span>
  );
}

function Anhaenge({ b }: { b: Daten }) {
  return (
    <>
      {b.anhaenge.map((a) => (
        <a className="bt-anhang" href={a.url} target="_blank" rel="noreferrer" key={a.url}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 3H6v18h12V7z" />
            <path d="M14 3v4h4" />
          </svg>
          <span className="bt-anhang-text">
            <span className="bt-anhang-name">{a.dateiname}</span>
            <span className="bt-anhang-ort">{a.ort || 'im Vorgangsordner'}</span>
          </span>
        </a>
      ))}
    </>
  );
}

export function Beitrag({
  b,
  ich,
  neu,
  mitLesestand,
  onAntworten,
  onAendern,
  onAnpinnen,
  suche = '',
}: {
  b: Daten;
  ich: string;
  /** Suchbegriff – im Text hervorgehoben (TEAM-CHAT.md, 12). */
  suche?: string;
  /** Vor diesem Beitrag steht die Trennlinie „Neu". */
  neu?: boolean;
  /** Im Chat ja, am Vorgang nein (TEAM-CHAT.md, Abschnitt 3). */
  mitLesestand?: boolean;
  onAntworten: (b: Daten) => void;
  onAendern: (b: Daten, text: string) => void;
  /** Nur im Team-Chat – am Vorgang gibt es keine Nadel. */
  onAnpinnen?: (b: Daten) => void;
}) {
  const [aendern, setAendern] = useState<string | null>(null);
  const [originalOffen, setOriginalOffen] = useState(false);

  return (
    <div className="bt-gruppe">
      {neu && (
        <span className="bt-trenner">
          <span className="bt-trenner-linie" />
          <span className="bt-trenner-text">Neu</span>
          <span className="bt-trenner-linie" />
        </span>
      )}

      <div className="bt">
        <span className={`bt-avatar ${b.eigen ? 'eigen' : ''}`}>{b.autorKurz}</span>
        <span className="bt-inhalt">
          <span className="bt-kopf">
            <span className="bt-name">{b.autor}</span>
            <span className="bt-zeit">{b.zeit}</span>
            {b.ausMonday && <span className="bt-herkunft">aus Monday übernommen</span>}
            {b.angepinnt && <span className="bt-nadel">angepinnt</span>}
          </span>

          {aendern === null ? (
            <Sprechblase b={b} ich={ich} suche={suche} />
          ) : (
            /*
             * Bearbeiten geschieht an Ort und Stelle. Ein Dialog dafür wäre
             * ein Fenster über dem Verlauf, und man sähe nicht mehr, worauf
             * sich der Satz bezieht.
             */
            <span className="bt-aendern">
              <textarea
                value={aendern}
                rows={2}
                autoFocus
                onChange={(e) => setAendern(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setAendern(null);
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && aendern.trim()) {
                    onAendern(b, aendern.trim());
                    setAendern(null);
                  }
                }}
              />
              <span className="bt-aendern-knoepfe">
                <button
                  type="button"
                  className="bt-knopf-prim"
                  disabled={!aendern.trim()}
                  onClick={() => {
                    onAendern(b, aendern.trim());
                    setAendern(null);
                  }}
                >
                  Speichern
                </button>
                <button type="button" className="bt-textlink" onClick={() => setAendern(null)}>
                  abbrechen
                </button>
              </span>
            </span>
          )}

          <Anhaenge b={b} />

          <span className="bt-fuss">
            <button type="button" className="bt-textlink" onClick={() => onAntworten(b)}>
              antworten
            </button>
            {b.eigen && aendern === null && (
              <button type="button" className="bt-textlink" onClick={() => setAendern(b.text)}>
                bearbeiten
              </button>
            )}
            {onAnpinnen && (
              <button type="button" className="bt-textlink" onClick={() => onAnpinnen(b)}>
                {b.angepinnt ? 'Nadel entfernen' : 'anpinnen'}
              </button>
            )}
            {mitLesestand && b.lesestand && (
              <span className="bt-gelesen">
                <IconHaken groesse={13} />
                {b.lesestand}
              </span>
            )}
            {b.bearbeitet && (
              <button
                type="button"
                className="bt-bearbeitet"
                title="Das Original einsehen"
                onClick={() => setOriginalOffen(!originalOffen)}
              >
                {b.bearbeitet}
              </button>
            )}
          </span>

          {/*
            Das Original bleibt abrufbar – Regel 1. Ein Verlauf, in dem
            still korrigiert werden kann, ist als Nachweis wertlos.
          */}
          {originalOffen && b.originalText && (
            <span className="bt-original">
              <span className="bt-original-marke">Ursprünglich</span>
              {b.originalText}
            </span>
          )}
        </span>
      </div>

      {b.antworten.map((a) => (
        <div className="bt bt--antwort" key={a.id}>
          <span className={`bt-avatar klein ${a.eigen ? 'eigen' : ''}`}>{a.autorKurz}</span>
          <span className="bt-inhalt">
            <span className="bt-kopf">
              <span className="bt-name klein">{a.autor}</span>
              <span className="bt-zeit">{a.zeit}</span>
              {a.ausMonday && <span className="bt-herkunft">aus Monday übernommen</span>}
            </span>
            <Sprechblase b={a} ich={ich} klein suche={suche} />
            <Anhaenge b={a} />
            {a.bearbeitet && <span className="bt-bearbeitet-still">{a.bearbeitet}</span>}
          </span>
        </div>
      ))}
    </div>
  );
}
